import { useCallback, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Checkbox,
  DropDown,
  FilterIcon,
  InputSearch,
  TableColumns,
  Text,
  Pagination,
  SettingIcon,
  TableColumnsColumnProps,
  Select,
  PlusIcon,
  ChartIcon,
  Tag,
} from '@mercai/clever'

import {
  TableColumnCreatedBy,
  TableColumnGeneratedData,
  TableColumnStatus,
  TableColumnAttendedAt,
  TableColumnMessages,
  TableColumnEvaluation,
  TableColumnScore,
  TableColumnJtbds,
  TableColumnCreatedAt,
  TableColumnTags,
  TableColumnDevelopmentActions,
  TableColumnAcquisitionActions,
} from '../table-columns'

import { ConversationDrawerFilter } from '../drawer-filter'

import { AppliedFilters } from '@/components'

import { DEFAULT_LIMIT_PAGE } from '@/constants'

import { useCookieState, isSuperUser } from '@/helpers'

import { useAuth } from '@/providers'

import { useAIEvaluationAllConversations } from '@/services'

import { IAConversationProps } from '@/types'

import { columnsForType } from './table.constants'

import { ConversationTableProps } from './table.type'

import styles from './table.module.css'

export const ConversationTable = ({
  type,
  data,
  isLoading,
  filter,
  onSetFilter,
  onHandleEditRegister,
  onHandleRegenerateConversation,
  onHandleNewRegister,
  onHandleShowRegister,
  onHandleDeleteRegister,
}: ConversationTableProps) => {
  const { t } = useTranslation('screenAiCenter')
  const { t: tEnums } = useTranslation('enums')
  const { whoAmI } = useAuth()

  const [isOpenDrawerFilter, setIsOpenDrawerFilter] = useState(false)
  const [totalPages, setTotalPages] = useState(1)

  const [columnsKeys, setColumnsKeys] = useCookieState<string[]>(
    `@mercai/conversation-${type}-columns-v2`,
    [...columnsForType[type]],
  )

  const getContentTotalFiltered = () => {
    const activeFilters = Object.keys(filter)?.filter(
      (key) => !!filter[key] && key !== 'page' && key !== 'limit',
    ).length

    if (activeFilters > 0) {
      return (
        <Tag variant="primary" model="light" size="small">
          {activeFilters}
        </Tag>
      )
    }

    return ''
  }

  const onHandleColumnsShow = (key: string) => {
    let newColumnsKeys = columnsKeys

    if (columnsKeys.includes(key)) {
      newColumnsKeys = columnsKeys.filter((item) => item !== key)
    } else {
      newColumnsKeys = [...columnsKeys, key]
    }

    setColumnsKeys(newColumnsKeys)
  }

  const getColumnsShow = useCallback(
    (key: string) => columnsKeys.includes(key),
    [columnsKeys],
  )

  const {
    mutate: evaluateAllConversations,
    isPending: isPendingEvaluateAllConversations,
  } = useAIEvaluationAllConversations({
    ...filter,
  })

  const handleFilter = (changedKey: string, newValue: string | number) => {
    if (changedKey === 'page') {
      onSetFilter({ ...filter, [changedKey]: `${newValue}` })
    } else {
      onSetFilter({ ...filter, [changedKey]: `${newValue}`, page: '1' })
    }
  }

  useEffect(() => {
    if (data?.count) {
      setTotalPages(
        Math.ceil(data?.count / +(filter?.limit || DEFAULT_LIMIT_PAGE)),
      )
    }
  }, [data])

  const columns: TableColumnsColumnProps<IAConversationProps>[] = [
    {
      title: t('conversationTableCreatedBy'),
      key: 'createdBy',
      dataKey: 'createdBy',
      render: ({ record }) => <TableColumnCreatedBy record={record} />,
    },
    {
      title: t('conversationTableGeneratedData'),
      key: 'generatedData',
      width: '12rem',
      dataKey: 'extraData',
      render: ({ record }) => <TableColumnGeneratedData record={record} />,
    },
    {
      title: t('conversationTableStatus'),
      width: '9rem',
      key: 'status',
      dataKey: 'status',
      render: ({ value }) => <TableColumnStatus value={value as string} />,
    },
    {
      title: t('conversationTableattendedByAcquisition'),
      key: 'attendedAt',
      width: '12rem',
      dataKey: 'attendedAt',
      render: ({ value, record }) => (
        <TableColumnAttendedAt value={value as string | null} record={record} />
      ),
    },
    {
      title: t('conversationTableMessages'),
      key: 'messages',
      width: '10rem',
      dataKey: 'messages',
      render: ({ value }) => (
        <TableColumnMessages value={value as unknown as unknown[]} />
      ),
    },
    {
      title: t('conversationTableTags'),
      key: 'tags',
      width: '10rem',
      dataKey: 'tags',
      render: ({ record }) => <TableColumnTags record={record} />,
    },
    {
      title: t('conversationTableOrigin'),
      key: 'origin',
      width: '9rem',
      dataKey: 'origin',
    },
    {
      title: t('conversationTableEvaluation'),
      key: 'jtbdCompleted',
      width: '9rem',
      dataKey: 'extraData',
      render: ({ record }) => <TableColumnEvaluation record={record} />,
    },
    {
      title: t('conversationTableScore'),
      key: 'score',
      width: '9rem',
      dataKey: 'extraData',
      render: ({ record }) => <TableColumnScore record={record} />,
    },
    {
      title: t('conversationTableJtbds'),
      key: 'jtbds',
      width: '14rem',
      dataKey: 'extraData',
      render: ({ record }) => <TableColumnJtbds record={record} />,
    },
    {
      title: t('conversationTableCreatedAt'),
      key: 'createdAt',
      width: '9rem',
      dataKey: 'createdAt',
      render: ({ value }) => (
        <TableColumnCreatedAt value={value as string | null} />
      ),
    },
    {
      title: t('conversationTableActions'),
      key: 'actionsDevelopment',
      dataKey: 'uuid',
      width: '16.5rem',
      render: ({ record }) => (
        <TableColumnDevelopmentActions
          record={record}
          onHandleRegenerateConversation={onHandleRegenerateConversation}
          onHandleEditRegister={onHandleEditRegister}
          onHandleShowRegister={onHandleShowRegister}
          onHandleDeleteRegister={onHandleDeleteRegister || (() => {})}
        />
      ),
    },
    {
      title: t('conversationTableActions'),
      key: 'actionsAcquisition',
      dataKey: 'uuid',
      width: '8rem',
      render: ({ record }) => (
        <TableColumnAcquisitionActions
          record={record}
          onHandleEditRegister={onHandleEditRegister}
          onHandleShowRegister={onHandleShowRegister}
        />
      ),
    },
  ]

  const memoizedColumns = useMemo(() => {
    const typeColumns = columnsForType[type] as readonly string[]
    const columnsFiltered = typeColumns?.reduce<
      TableColumnsColumnProps<IAConversationProps>[]
    >((acc, current) => {
      const column = columns?.find((column) => column.key === current)

      if (column && getColumnsShow(column.key)) {
        return [...acc, column]
      }

      return acc
    }, [])

    return columnsFiltered
  }, [columns, getColumnsShow, type])

  return (
    <div className={styles.core}>
      <div className={styles.filter}>
        <div className={styles['filter-group']}>
          <InputSearch
            value={filter.search as string}
            onChange={(value) => handleFilter('search', value)}
            className={styles['search-input']}
            translations={{
              inputPlaceholder: t('searchFilterPlaceholder'),
            }}
          />{' '}
          <Button
            variant="secondary"
            onClick={() => setIsOpenDrawerFilter(true)}
          >
            <FilterIcon />
            {t('conversationTableFilterButton')}
            {getContentTotalFiltered()}
          </Button>
        </div>

        <div className={styles['filter-group']}>
          <Button onClick={onHandleNewRegister}>
            <PlusIcon />
            {t('conversationTableNewRegister')}
          </Button>

          {isSuperUser(whoAmI?.email) && type === 'development' && (
            <Button
              variant="secondary"
              model="square"
              onClick={() => evaluateAllConversations()}
              disabled={isPendingEvaluateAllConversations}
            >
              <ChartIcon />
            </Button>
          )}

          <DropDown.Root>
            <DropDown.ButtonTrigger
              variant="secondary"
              model="square"
              hasArrow={false}
            >
              <SettingIcon />
            </DropDown.ButtonTrigger>

            <DropDown.Content orientation="bottom-right">
              {columns?.map((column) => (
                <DropDown.Item key={column.key}>
                  <Checkbox
                    onChange={() => onHandleColumnsShow(column.key)}
                    value={getColumnsShow(column.key)}
                  >
                    {column.title}
                  </Checkbox>
                </DropDown.Item>
              ))}
            </DropDown.Content>
          </DropDown.Root>
        </div>
      </div>

      <AppliedFilters
        translations={{
          filterAppliedText: t('conversationTableFilterAppliedText'),
        }}
        filtersApplied={filter}
        onRemoveFilter={(key) => handleFilter(key, '')}
        keyTranslations={{
          isNotificationOnly: t('conversationTableFilterIsNotificationOnly'),
          search: t('conversationTableFilterSearch'),
          role: t('conversationTableFilterRole'),
          rfqReference: t('conversationTableFilterRfqSearch'),
        }}
        renderValue={(key: string, value: string) => {
          if (key === 'isNotificationOnly') {
            return value === 'true'
              ? t('conversationTableFilterIsNotificationOnlyYes')
              : t('conversationTableFilterIsNotificationOnlyNo')
          }

          if (key === 'role') {
            return tEnums(`conversation_role_${value}`)
          }

          return value
        }}
        excludeKeys={[
          'page',
          'limit',
          'uuid',
          'show',
          'form',
          'buyerPhones',
          'buyer_profile',
          'tab',
          'new',
        ]}
      />

      <TableColumns<IAConversationProps>
        isLoading={isLoading}
        columns={memoizedColumns}
        data={data?.results || []}
        classNames={{
          bodyColumn: styles['body-column'],
        }}
      />

      <div className={styles.pagination}>
        <div className={styles['pagination-show-registers']}>
          <Text>
            {t('conversationTableShowRegisters', {
              showNumber:
                (data?.count || 0) < +filter.limit
                  ? data?.count
                  : +filter.limit,
              totalNumber: data?.count || 0,
            })}
          </Text>

          <Select
            value={filter.limit}
            onChange={(value) => handleFilter('limit', value)}
            options={[
              { label: '10', value: '10' },
              { label: '20', value: '20' },
              { label: '50', value: '50' },
              { label: '100', value: '100' },
            ]}
          />
        </div>

        <Pagination
          totalPages={totalPages}
          onChange={(page) => handleFilter('page', page)}
          currentPage={+filter.page as number}
        />
      </div>

      <ConversationDrawerFilter
        isOpen={isOpenDrawerFilter}
        onHandleOpen={() => setIsOpenDrawerFilter(false)}
        filter={filter}
        onHandleFilter={onSetFilter}
      />
    </div>
  )
}
